import React, { useEffect, useRef } from 'react'
import ApexCharts, { ApexOptions } from 'apexcharts'
import { toAbsoluteUrl } from '../../../_metronic/helpers'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCircleCheck } from '@fortawesome/free-solid-svg-icons'
import { useThemeMode } from '../../../_metronic/partials/layout/theme-mode/ThemeModeProvider'
import { formatNumber } from '../../../reusable'
import { getCSS, getCSSVariableValue } from '../../../_metronic/assets/ts/_utils'
import { Tr } from '../../../_metronic/i18n/Tr'

/**
 * A single tooltip field shown on hover.
 * - `key`   : the property name inside each data point object (e.g. "totalAds")
 * - `label` : human-readable label shown in the tooltip (e.g. "Total Ads")
 * - `format`: optional – "number" runs formatNumber(), "raw" shows as-is (default: "number")
 */
export interface TooltipField {
  key: string
  label: string
  format?: 'number' | 'raw'
}

export interface DynamicChartProps {
  /** CSS class for the card wrapper */
  className?: string
  /** The heading text above the total (e.g. "Total Spent", "Total Revenue") */
  title: string
  /** Formatted total value displayed prominently */
  totalValue: string
  /** X-axis category labels */
  categories: string[]
  /**
   * Chart series – each data point is a plain object with at least `x` (string) and `y` (number).
   * Any extra keys (totalAds, totalSpent, commission, …) are available via tooltipFields.
   */
  data: { name: string; data: Record<string, any>[] }[]
  /** Which extra fields to show in the tooltip on hover */
  tooltipFields: TooltipField[]
  /** If "0" or empty, an empty-state message is shown instead of the chart */
  length?: string
  /** Message to display when there is no data */
  emptyMessage?: string
  /** Bar column width percentage (default 40%) */
  columnWidth?: string
  /** Override bar color (CSS variable name). Default: --greencolorprofit */
  barColor?: string
  /** Optional children rendered above the chart (e.g. filter controls) */
  children?: React.ReactNode
}

const DynamicChart: React.FC<DynamicChartProps> = ({
  className = '',
  title,
  totalValue,
  categories,
  data,
  tooltipFields,
  length = '0',
  emptyMessage,
  columnWidth = '40%',
  barColor,
  children,
}) => {
  const t = Tr()
  const chartRef = useRef<HTMLDivElement | null>(null)
  const { mode } = useThemeMode()

  useEffect(() => {
    if (!chartRef.current || length === '0') return

    const height = parseInt(getCSS(chartRef.current, 'height')) || 350
    const chart = new ApexCharts(
      chartRef.current,
      buildChartOptions(height, data, categories, tooltipFields, columnWidth, barColor, t)
    )
    chart.render()

    return () => {
      chart.destroy()
    }
  }, [chartRef, mode, data, categories, tooltipFields, length])

  return (
    <div className={`card ${className} position-relative`}>
      <img
        src={toAbsoluteUrl('/media/otherSVG/Group148.svg')}
        alt=''
        className='vs-chart-bg-img'
      />

      <div className='card-header border-0 pt-5 vs-chart-header'>
        <h3 className='card-title align-items-start flex-column'>
          <span className='card-label fw-bold fs-3 mb-1 d-flex align-items-center'>
            <FontAwesomeIcon icon={faCircleCheck} className='fontsize35px me-3 themeGreenColor' />
            <span className='fs-3 fw-bold'>{t(title)}</span>
          </span>
        </h3>
        <label className='fontsize48px fontweight700 w-100 themeBlueColor'>
          {length === '0' ? '0' : formatNumber(parseFloat(totalValue))}
        </label>
      </div>

      {children}

      {length !== '0' ? (
        <div className='card-body'>
          <div
            ref={chartRef}
            className='mt-10 vs-chart-container'
          />
        </div>
      ) : (
        <div className='d-flex justify-content-center align-items-center'>
          <h1 className='mt-10 mb-10 text-muted fs-4'>
            {emptyMessage || t('No Data Available')}
          </h1>
        </div>
      )}
    </div>
  )
}

export { DynamicChart }

// ── Build ApexCharts options dynamically ──
function buildChartOptions(
  height: number,
  data: { name: string; data: Record<string, any>[] }[],
  categories: string[],
  tooltipFields: TooltipField[],
  columnWidth: string,
  barColor: string | undefined,
  t: (key: string) => string
): ApexOptions {
  const labelColor = getCSSVariableValue('--bs-gray-500')
  const borderColor = getCSSVariableValue('--bs-gray-200')
  const baseColor = barColor
    ? getCSSVariableValue(barColor)
    : getCSSVariableValue('--greencolorprofit')

  return {
    series: data as any,
    chart: {
      fontFamily: 'inherit',
      type: 'bar',
      height,
      toolbar: { show: false },
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth,
        borderRadius: 5,
        rangeBarOverlap: false,
        dataLabels: {
          position: 'top',
        },
      },
    },
    legend: { show: false },
    dataLabels: {
      enabled: true,
      formatter: (val: number) => {
        if (val === 0) return ''
        return formatNumber(val)
      },
      offsetY: -20,
      style: {
        fontSize: '11px',
        fontWeight: 600,
        colors: [labelColor],
      },
    },
    stroke: {
      show: true,
      width: 2,
      colors: ['transparent'],
    },
    xaxis: {
      categories,
      axisBorder: { show: false },
      axisTicks: { show: false },
      labels: {
        style: { colors: labelColor, fontSize: '12px' },
      },
    },
    yaxis: {
      labels: {
        style: { colors: labelColor, fontSize: '12px' },
        formatter: (val) => val.toFixed(2),
      },
    },
    fill: { opacity: 1 },
    states: {
      normal: { filter: { type: 'none', value: 0 } },
      hover: { filter: { type: 'none', value: 0 } },
      active: {
        allowMultipleDataPointsSelection: false,
        filter: { type: 'none', value: 0 },
      },
    },
    tooltip: {
      shared: true,
      intersect: false,
      y: {
        formatter: function (val: any, { seriesIndex, dataPointIndex, w }) {
          const point = w.config.series[seriesIndex].data[dataPointIndex]

          // Build tooltip HTML from the dynamic fields
          const lines = tooltipFields.map((field) => {
            const raw = point[field.key]
            const formatted =
              field.format === 'raw'
                ? raw
                : formatNumber(parseFloat(String(raw ?? 0)))
            return `<span>${t(field.label)}:</span> <span style="color: black; font-weight: bold;">${formatted}</span>`
          })

          return lines.join('<br>')
        },
      },
    },
    colors: [baseColor],
    grid: {
      borderColor,
      strokeDashArray: 4,
      yaxis: { lines: { show: true } },
    },
  }
}
